import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Form, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { useHost } from 'src/providers/HostProvider';
import { getAdminState } from 'src/store/admin/selectors';
import { deletePlace, savePlace } from 'src/store/admin/services';
import { adminActions } from 'src/store/admin/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { BookingObject, PlaceType } from 'src/types';

import { labelsByBookingObjectType } from '../consts';
import { toBookingObjectType } from '../utils';

import classes from './AdminBottomSheet.module.scss';
import { DeskFields } from './BookingObjects/Fields/DeskFields';
import { ParkingFields } from './BookingObjects/Fields/ParkingFields';
import { RoomFields } from './BookingObjects/Fields/RoomFields';

interface InnerProps {
  bookingObject: BookingObject;
}

const Inner: FC<InnerProps> = ({ bookingObject }) => {
  switch (bookingObject.type) {
    case PlaceType.Desk:
      return <DeskFields bookingObject={bookingObject} />;
    case PlaceType.Room:
      return <RoomFields bookingObject={bookingObject} />;
    case PlaceType.Parking:
      return <ParkingFields bookingObject={bookingObject} />;
    default:
      return null;
  }
};

interface FieldsProps {
  bookingObject?: BookingObject;
}

const Fields: FC<FieldsProps> = ({ bookingObject }) => {
  const existing = !!bookingObject?.id;

  const dispatch = useAppDispatch();

  const { submit, reset } = useForm();

  const { bookingObjectIsOpened } = useSelector(getAdminState);

  const close = useCallback(() => {
    dispatch(adminActions.closeBookingObject());
    dispatch(adminActions.clearSelectedBookingObject());
    reset();
  }, [dispatch, reset]);

  const saveBookingObject = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');

    close();
  }, [close, submit]);

  const deleteBookingObject = useCallback(async () => {
    await dispatch(deletePlace());

    close();
  }, [close, dispatch]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    const result: BottomSheetActionsType = [
      {
        label: existing ? 'Сохранить' : 'Создать',
        variant: 'contained',
        onClick: saveBookingObject,
      },
    ];
    if (existing)
      result.unshift({
        label: 'Удалить',
        variant: 'outlined',
        onClick: deleteBookingObject,
      });

    return result;
  }, [deleteBookingObject, existing, saveBookingObject]);

  const title = bookingObject
    ? labelsByBookingObjectType[
        toBookingObjectType(
          bookingObject.type,
          bookingObject.type === PlaceType.Room
            ? bookingObject.roomType
            : undefined,
        )
      ]
    : 'Объект бронирования';

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(adminActions.setBookingObjectIsOpened(value));
      dispatch(adminActions.clearSelectedBookingObject());
      reset();
    },
    [dispatch, reset],
  );

  return (
    <BottomSheet
      isOpen={bookingObjectIsOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[70, 0.5, 1]}
      className={classes.settings}
      initialSnapPoint={2}
      header={{
        icon: Icons.Pin,
        title,
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        {bookingObject && <Inner bookingObject={bookingObject} />}
      </div>
    </BottomSheet>
  );
};

export const AdminBottomSheet: FC = () => {
  const { host } = useHost();
  const { selectedPlace } = useSelector(getAdminState);

  const dispatch = useAppDispatch();

  const save = useCallback(async () => {
    await dispatch(savePlace());
    return true;
  }, [dispatch]);

  if (!host) return null;

  return createPortal(
    <Form onSubmit={save} initialValues={selectedPlace}>
      {() => <Fields bookingObject={selectedPlace} />}
    </Form>,
    host,
  );
};
